/*
 * Cart drawer recommendations ("Customers who bought this item also bought").
 *
 * - Results are cached per URL for the life of the page, so every drawer re-render
 *   (add, quantity change, remove) does NOT fire another /recommendations request.
 *   Shopify rate-limits that endpoint and answers 429 when it is hit repeatedly,
 *   which is what left the heading on screen with no products underneath.
 * - A response is only applied if this element is still in the document (the drawer
 *   may have been re-rendered while the request was in flight).
 * - If there is nothing to show (no products, or the request failed) the whole block,
 *   including the heading, is removed instead of leaving an empty heading behind.
 */
(function () {
  const cache = new Map();      // url -> innerHTML string ('' = nothing to show)
  const inflight = new Map();   // url -> Promise<string>

  const load = (url) => {
    if (cache.has(url)) return Promise.resolve(cache.get(url));
    if (inflight.has(url)) return inflight.get(url);

    const promise = fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);
        return response.text();
      })
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cart-recommendations');
        const inner = recommendations && recommendations.querySelector('li')
          ? recommendations.innerHTML
          : '';
        cache.set(url, inner);
        return inner;
      })
      .finally(() => inflight.delete(url));

    inflight.set(url, promise);
    return promise;
  };

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.url = this.dataset.url;

      if (cache.has(this.url)) {
        this.render(cache.get(this.url));
        return;
      }

      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this));
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    handleIntersection(entries, observer) {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      load(this.url)
        .then(inner => this.render(inner))
        .catch(e => {
          console.error(e);
          this.render('');
        });
    }

    render(inner) {
      if (!this.isConnected) return;
      if (inner) {
        this.innerHTML = inner;
        this.revealImages();
      } else {
        this.remove();
      }
    }

    // The cached markup can carry the theme's "loading" class from when it was first parsed. If the thumbnail is
    // already in the browser cache its load event never fires again, leaving the grey loading bar in place.
    // Load the (small) thumbnails straight away and clear the loading state once each one is ready.
    revealImages() {
      this.querySelectorAll('.media-wrapper').forEach((wrapper) => {
        const img = wrapper.querySelector('img');
        if (!img) { wrapper.classList.remove('loading'); return; }
        img.loading = 'eager';
        const done = () => wrapper.classList.remove('loading');
        if (img.complete) { done(); return; }
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }
  }

  if (!customElements.get('cart-recommendations')) {
    customElements.define('cart-recommendations', CartRecommendations);
  }
})();